export default {
  title: 'Analytics',
  description: 'Track performance and insights',
  overview: 'Overview',
  coverage: {
    title: 'Coverage',
    description: 'Overview of your content performance and audience engagement'
  },
  errors: {
    loadFailed: 'Failed to load coverage stats'
  },
  period: {
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    custom: 'Custom Range'
  },
  dateRange: 'Date Range',
  startDate: 'Start Date',
  endDate: 'End Date',
  quickShortcuts: 'Quick Shortcuts',
  shortcuts: {
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    lastYear: 'Last year'
  },
  metrics: {
    impressions: {
      label: 'Impressions',
      description: 'Number of times your content was displayed to users'
    },
    views: {
      label: 'Views',
      description: 'Number of times your content was actually played or viewed'
    },
    uniqueViewers: {
      label: 'Unique Viewers',
      description: 'Number of distinct users who viewed your content'
    },
    avgViewDuration: {
      label: 'Avg. View Duration',
      description: 'Average time spent viewing your content per session'
    },
    totalWatchTime: {
      label: 'Total Watch Time',
      description: 'Total accumulated time spent viewing all your content'
    },
    clickThroughRate: {
      label: 'Click-through Rate',
      description: 'Percentage of impressions that resulted in views (Views / Impressions)'
    },
    avgCompletionRate: {
      label: 'Avg. Completion Rate',
      description: 'Average percentage of content watched before users stop viewing'
    },
    assetsCount: {
      label: 'Media',
      description: 'Number of media files included in these statistics'
    },
    growth: 'Growth',
    sessions: 'Sessions',
    bounceRate: 'Bounce Rate',
    pagesPerSession: 'Pages per Session',
    newUsersPercentage: 'New Users'
  },
  granularity: {
    hour: 'Hour',
    day: 'Day',
    week: 'Week',
    month: 'Month'
  },
  charts: {
    timeSeries: 'Views & Impressions',
    uniqueViewersOverTime: 'Unique Viewers',
    watchTimeOverTime: 'Watch Time',
    deviceDistribution: 'Device Distribution',
    worldHeatmap: 'Geographic Distribution',
    topAssets: 'Top Assets',
    topCountries: 'Top Countries',
    topReferrers: 'Top Referrers',
    topTags: 'Top Tags'
  },
  origin: {
    worldHeatmap: {
      title: 'Views by Country'
    }
  },
  geography: {
    title: 'Geographic Analytics',
    description: 'Discover where your audience is located and track geographic trends',
    topCountries: 'Top Countries',
    views: 'views',
    regionalDistribution: 'Regional Distribution',
    topCities: 'Top Cities',
    totalCountries: 'Total Countries',
    topCountry: 'Top Country',
    topRegion: 'Top Region',
    avgViewsPerCountry: 'Avg. Views per Country',
    country: 'Country',
    percentage: 'Share',
    growth: 'Growth',
    viewAll: 'View All',
    totalCountriesDesc: 'Number of countries viewing your content',
    ofTotalViews: 'of total views',
    avgViewsPerCountryDesc: 'Average views from each country'
  },
  trafficSources: {
    title: 'Traffic Sources',
    description: 'Understand how visitors find and interact with your content',
    trafficDistribution: 'Traffic Distribution',
    topSources: 'Top Sources',
    allReferrers: 'All Referrers',
    views: 'Views',
    impressions: 'Impressions',
    viewers: 'Unique Viewers',
    direct: 'Direct Traffic',
    directDesc: 'Visitors who typed your URL directly',
    searchEngines: 'Search Engines',
    socialMedia: 'Social Media',
    otherWebsites: 'Other Websites',
    referralDesc: 'External websites and blogs',
    ofTotal: 'of total',
    directTraffic: 'Direct',
    totalReferrers: 'Total Referrers',
    totalReferrersDesc: 'Number of different traffic sources',
    topReferrer: 'Top Referrer',
    topReferrerDesc: 'Most traffic from this source',
    directTrafficDesc: 'Percentage of direct visitors',
    socialTraffic: 'Social Traffic',
    socialTrafficDesc: 'Traffic from social networks',
    source: 'Source',
    ctr: 'CTR',
    avgDuration: 'Avg Duration',
    completion: 'Completion',
    export: 'Export',
    social: 'Social',
    search: 'Search',
    sortByViews: 'Sort by Views',
    sortByImpressions: 'Sort by Impressions',
    sortByCTR: 'Sort by CTR',
    sortByViewers: 'Sort by Viewers',
    searchReferrers: 'Search referrers...',
    referrerDomain: 'Referrer Domain',
    uniqueViewers: 'Unique Viewers',
    trafficShare: 'Traffic Share',
    campaign: 'Campaign',
    utmDescription: 'Campaign performance tracked via UTM parameters'
  },
  filters: {
    title: 'Filters',
    apply: 'Apply Filters',
    clear: 'Clear All',
    dateRange: 'Date Range',
    devices: 'Devices',
    countries: 'Countries',
    referrers: 'Referrers',
    tags: 'Tags'
  },
  reviews: {
    title: 'Reviews',
    description: 'Feedback and ratings from your viewers',
    basedOn: 'Based on {count} reviews',
    sortBy: 'Sort by',
    allRatings: 'All ratings',
    all: 'All',
    export: 'Export',
    comments: 'Comments',
    recentReviews: 'Recent Reviews',
    helpful: 'Helpful',
    reply: 'Reply',
    noReviews: 'No reviews yet',
    noReviewsDesc: 'Reviews will appear here once viewers start rating your content',
    noComment: 'No comment provided',
    mostRecent: 'Most recent',
    byRating: 'By rating',
    oldest: 'Oldest',
    ratingAsc: 'Rating ascending',
    ratingDesc: 'Rating descending',
    assetsReviewed: 'Assets Reviewed',
    avgReviewsPerAsset: '{avg} reviews per asset',
    avgRating: 'Average Rating',
    avgRatingDesc: 'Average rating across all reviews',
    totalReviews: 'Total Reviews',
    totalReviewsDesc: 'Total number of reviews received',
    assetsReviewedDesc: 'Number of unique assets with reviews',
    reviewsPerAsset: 'Reviews per Asset',
    reviewsPerAssetDesc: 'Average number of reviews per asset',
    satisfaction: 'Satisfaction',
    satisfactionDesc: 'Overall satisfaction score',
    ratingBreakdown: 'Rating Breakdown',
    ratingDistribution: 'Rating Distribution',
    insights: 'Insights',
    positiveReviews: 'Positive reviews',
    neutralReviews: 'Neutral reviews',
    negativeReviews: 'Negative reviews',
    responseRate: 'Response rate',
    reviews: 'reviews',
    anonymous: 'Anonymous',
    contact: 'Contact',
    viewAsset: 'View Asset',
    loadMore: 'Load More',
    loadingReviews: 'Loading reviews...'
  },
  topAssets: {
    title: 'Top Assets',
    description: 'Track the performance of your best performing content',
    sortBy: 'Sort by',
    show: 'Show',
    days: 'days',
    allTime: 'All Time',
    assetName: 'Asset Name',
    views: 'Views',
    impressions: 'Impressions',
    ctr: 'CTR',
    uniqueViewers: 'Unique Viewers',
    avgDuration: 'Avg Duration',
    totalDuration: 'Total Duration',
    completion: 'Completion',
    trend: 'Trend',
    viewDetails: 'View Details',
    fullRanking: 'Full Ranking',
    noAssets: 'No assets found',
    noAssetsDesc: 'Upload assets to start tracking their performance',
    metrics: {
      views: 'Views',
      impressions: 'Impressions',
      uniqueViewers: 'Unique Viewers',
      avgDuration: 'Average Duration',
      totalDuration: 'Total Duration',
      completion: 'Completion Rate',
      ctr: 'Click-through Rate'
    }
  },
  export: {
    title: 'Export Data',
    format: 'Format',
    button: 'Export',
    excel: 'Excel (.xlsx)',
    csv: 'CSV (.csv)',
    download: 'Download Report'
  },
  videoHeatmap: {
    title: 'Video Heatmap',
    description: 'Analyze viewer engagement patterns throughout your videos',
    selectVideo: 'Select a video',
    timeRange: 'Time range',
    export: 'Export Data',
    totalViews: 'Total Views',
    avgCompletion: 'Avg Completion',
    avgWatchTime: 'Avg Watch Time',
    dropOffRate: 'Drop-off Rate',
    engagementHeatmap: 'Engagement Heatmap',
    viewerEngagement: 'Viewer Engagement',
    highEngagement: 'High',
    mediumEngagement: 'Medium',
    lowEngagement: 'Low',
    basedOn: 'Based on {count} views',
    recentSessions: 'Recent Sessions',
    sessionId: 'Session ID',
    user: 'User',
    watchPattern: 'Watch Pattern',
    watchTime: 'Watch Time',
    completion: 'Completion',
    engagement: 'Engagement',
    date: 'Date',
    viewers: 'Viewers',
    noVideoSelected: 'No Video Selected',
    selectVideoPrompt: 'Select a video from the dropdown to view its heatmap',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    allTime: 'All time',
    regions: 'regions'
  },
  geography: {
    title: 'Geographic Analytics',
    description: 'Discover where your audience is located and track geographic trends',
    topCountries: 'Top Countries',
    views: 'views',
    regionalDistribution: 'Regional Distribution',
    continentalDistribution: 'Continental Distribution',
    topCities: 'Top Cities',
    totalCountries: 'Total Countries',
    topCountry: 'Top Country',
    topContinent: 'Top Continent',
    topRegion: 'Top Region',
    avgViewsPerCountry: 'Avg. Views per Country',
    country: 'Country',
    percentage: 'Share',
    growth: 'Growth',
    viewAll: 'View All',
    totalCountriesDesc: 'Number of countries viewing your content',
    topCountryDesc: 'Country with most views',
    topContinentDesc: 'Continent with most views',
    ofTotalViews: 'of total views',
    avgViewsPerCountryDesc: 'Average views from each country',
    uniqueViewers: 'Unique Viewers',
    ctr: 'CTR',
    avgDuration: 'Avg Duration',
    noData: 'No geographic data',
    noDataDesc: 'Geographic data will appear once your content receives views',
    regions: 'regions'
  },
  users: {
    title: 'User Statistics',
    description: 'Monitor user activity, engagement and behavior patterns'
  },
  sales: {
    title: 'Sales Analytics',
    description: 'Track revenue, transactions and monetization performance'
  },
  technical: {
    title: 'Technical Analytics',
    description: 'Monitor system performance, resource usage and infrastructure metrics'
  },
  noData: 'No data available for the selected period',
  loading: 'Loading analytics data...',
  error: 'Failed to load analytics data'
}
